import { useEffect, useId, useState } from 'react';
import { Info } from 'lucide-react';
import { displayName, formatDayLabel, formatOutcome, formatPhone, formatTime, phoneDigits } from '@/lib/conversationFormat';
import conversationService, { type ConversationDetailResponse } from '@/services/conversationService';
import ChatHeader, { TestMark } from './ChatHeader';
import { SessionContext, type AgentLabel } from './ContextPanel';
import MessageTimeline from './MessageTimeline';
import { useServerNow } from './useServerNow';
import { ChatError, ChatSkeleton, WorkspaceFrame, type WorkspaceChrome } from './WorkspaceFrame';

type Props = WorkspaceChrome & {
  conversationId: number;
  me: string | null;
  agentLabel?: AgentLabel;
  /** Already fetched (the live call view preloads the stored transcript). */
  initialDetail?: ConversationDetailResponse | null;
  /** Contact of this conversation, when the list already knows it. */
  knownContactKey?: string | null;
  /** A WhatsApp session belongs to this contact: open the continuous chat there. */
  onResolveContact: (contactKey: string, conversationId: number) => void;
};

/**
 * The contact behind a stored WhatsApp session, from the server only (the web
 * app never builds a key): the detail's `contact_key`, else the one
 * `group=contact` row for exactly this number.
 */
export async function resolveContactKey(
  detail: ConversationDetailResponse,
  conversationId: number,
): Promise<string | null> {
  if (detail.contact_key) return detail.contact_key;
  const res = await conversationService.list('whatsapp', detail.customer_phone, 20, 0, undefined, 'contact');
  const digits = phoneDigits(detail.customer_phone);
  const matches = (res.items ?? []).filter(
    (i) => i.type === 'whatsapp' && !!i.contact_key && phoneDigits(i.customer_phone) === digits,
  );
  if (matches.length === 1) return matches[0].contact_key ?? null;
  // Several agent lines for this number: only an exact session match is safe.
  return matches.find((i) => i.id === conversationId)?.contact_key ?? null;
}

type Phase = 'loading' | 'resolving' | 'ready' | 'error';

/**
 * One stored conversation (`?c=<id>`): a finished phone call — or an old
 * WhatsApp link, which opens the contact's continuous chat instead.
 */
export function SessionWorkspace({
  conversationId,
  agentLabel,
  me,
  initialDetail = null,
  knownContactKey = null,
  onResolveContact,
  layout,
  contextOpen,
  onContextOpenChange,
  onBack,
}: Props) {
  const headingId = useId();
  const now = useServerNow(60_000);
  const [detail, setDetail] = useState<ConversationDetailResponse | null>(initialDetail);
  const [phase, setPhase] = useState<Phase>(initialDetail?.record.type === 'phone' ? 'ready' : 'loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (knownContactKey) {
      onResolveContact(knownContactKey, conversationId);
      return undefined;
    }
    (async () => {
      try {
        const loaded =
          initialDetail && initialDetail.record.id === conversationId
            ? initialDetail
            : await conversationService.getDetail(conversationId);
        if (cancelled) return;
        if (loaded.record.type === 'whatsapp') {
          setPhase('resolving');
          const key = await resolveContactKey(loaded, conversationId).catch(() => null);
          if (cancelled) return;
          if (key) {
            onResolveContact(key, conversationId);
            return;
          }
        }
        setDetail(loaded);
        setPhase('ready');
      } catch {
        if (!cancelled) setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload per conversation / retry only
  }, [conversationId, knownContactKey, attempt]);

  if (phase === 'error') {
    return (
      <ChatError message="Verlauf konnte nicht geladen werden." onRetry={() => setAttempt((n) => n + 1)} onBack={onBack} />
    );
  }
  if (phase !== 'ready' || !detail) return <ChatSkeleton onBack={onBack} />;

  const record = detail.record;
  const isPhone = record.type === 'phone';
  const started = record.started_at ?? record.last_message_at;
  const outcome = formatOutcome(record.status);
  const hasSpokenTurns = record.messages.some((m) => m.role !== 'event');

  const chat = (
    <>
      <ChatHeader
        headingId={headingId}
        name={displayName(detail.customer_name, detail.customer_phone)}
        avatarName={detail.customer_name}
        avatarPhone={detail.customer_phone}
        phoneCall={isPhone}
        badges={record.is_test ? <TestMark /> : null}
        meta={[
          detail.customer_name?.trim() ? formatPhone(detail.customer_phone) : null,
          isPhone ? 'Telefonat' : 'WhatsApp-Sitzung',
          started ? `${formatDayLabel(started, now)} ${formatTime(started)}` : null,
          outcome,
        ]
          .filter(Boolean)
          .join(' · ')}
        onBack={onBack}
        onToggleContext={() => onContextOpenChange(!contextOpen)}
        contextOpen={contextOpen}
      />
      {!isPhone && (
        <p className="flex flex-shrink-0 items-start gap-2 border-b border-border bg-muted/60 px-5 py-2 text-xs text-muted-foreground">
          <Info className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          Diese Sitzung ließ sich keinem WhatsApp-Kontakt zuordnen – sie wird einzeln und nur zum Lesen angezeigt.
        </p>
      )}
      {isPhone && record.messages.length > 0 && !hasSpokenTurns && (
        <p className="flex-shrink-0 border-b border-border bg-muted/60 px-5 py-2 text-xs text-muted-foreground">
          In diesem Anruf wurde nichts gesprochen – unten steht, was das System getan hat.
        </p>
      )}
      <MessageTimeline
        messages={record.messages}
        sessions={[]}
        channel={record.type}
        me={me}
        emptyText="Kein Transkript verfügbar"
      />
    </>
  );

  return (
    <WorkspaceFrame
      layout={layout}
      contextOpen={contextOpen}
      onContextOpenChange={onContextOpenChange}
      onBack={onBack}
      labelledBy={headingId}
      chat={chat}
      context={<SessionContext detail={detail} agentLabel={agentLabel} />}
    />
  );
}

export default SessionWorkspace;
